import { USER_STROLLERS_DELETE_CONFIRM } from 'mk/autogenerated/translations/ProfileProducts.sagas.6f52073ff08043a6f8b831d3718c4446'
import {
    strollers_api_suggest_strollers2_url,
    strollers_api_v2_user_strollers_add_user_stroller_url,
    strollers_api_v2_user_strollers_remove_user_stroller_url,
} from 'mk/urls/functions';
import { Option } from 'mk2/apps/blogs/components/MyProducts';
import {
    profileProductsAddProductApi,
    profileProductsRemoveProductApi,
    profileProductsSearchProductsApi,
    ProfileProductsAddProductAction,
    ProfileProductsAddProductApiNormalizedResponse,
    ProfileProductsRemoveProductAction,
    ProfileProductsSearchProductsAction,
    PROFILE_PRODUCTS_ADD_PRODUCT,
    PROFILE_PRODUCTS_REMOVE_PRODUCT,
    PROFILE_PRODUCTS_SEARCH_PRODUCTS,
} from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.actions';
import { strollersAddReviewUrl } from 'mk2/apps/strollers/urls';
import { handleXHRGetErrorSaga, normalizeError, XHRAction, XHRGetError } from 'mk2/helpers/api';
import { url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { ProfileProductType, StrollerEntity, StrollerSchema } from 'mk2/schemas';
import { getDenormalizedEntity, getRoutingLocation } from 'mk2/selectors';
import { pushInPWASaga } from 'mk2/services/browserHistory';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('blogs/ProfileProducts.sagas');

function* removeProduct({ username, productType, productId, xhr }: ProfileProductsRemoveProductAction & XHRAction) {
    // TODO: Extend for multiple products
    if (productType === ProfileProductType.STROLLER) {
        if (!window.confirm(USER_STROLLERS_DELETE_CONFIRM)) {
            return;
        }

        yield put(profileProductsRemoveProductApi.request(username, productType, productId));

        try {
            yield call(() =>
                xhr.post(strollers_api_v2_user_strollers_remove_user_stroller_url(), {
                    strollerId: productId,
                    username,
                }),
            );
            yield put(profileProductsRemoveProductApi.success(username, productType, productId));
        } catch (error) {
            logger.error(error);
            yield put(profileProductsRemoveProductApi.failure(username, productType, productId, normalizeError(error)));
        }
    }
}

export interface ApiResponseAddProduct {
    body: {
        entities: ProfileProductsAddProductApiNormalizedResponse['entities'];
        result: ProfileProductsAddProductApiNormalizedResponse['result'];
        hasReviewed: boolean;
    };
}

function* addProduct({ username, productType, productId, xhr }: ProfileProductsAddProductAction & XHRAction) {
    // TODO: Extend for multiple products
    if (productType === ProfileProductType.STROLLER) {
        yield put(profileProductsAddProductApi.request(username, productType, productId));

        try {
            const response: ApiResponseAddProduct = yield call(() =>
                xhr.post(strollers_api_v2_user_strollers_add_user_stroller_url(), { strollerId: productId, username }),
            );
            const normalizedResponse = {
                entities: response.body.entities,
                result: response.body.result,
            };
            yield put(profileProductsAddProductApi.success(username, productType, productId, normalizedResponse));

            // Redirect to add new review if user not reviewed this stroller
            const stroller = getDenormalizedEntity<StrollerEntity>(
                normalizedResponse,
                StrollerSchema,
                response.body.result.stroller,
            );
            if (!response.body.hasReviewed) {
                const location = yield select(getRoutingLocation);
                yield pushInPWASaga(
                    url(strollersAddReviewUrl, { slug: stroller.slug }, { source: 'profile-products', next: location.pathname }),
                );
            }
        } catch (error) {
            logger.error(error);
            yield put(profileProductsAddProductApi.failure(username, productType, productId, normalizeError(error)));
        }
    }
}

interface ApiResponseSearchProducts {
    body: {
        suggestedStrollerOptions: Option[];
    };
}

function* searchProducts({ productType, term, xhr }: ProfileProductsSearchProductsAction & XHRAction) {
    if (productType === ProfileProductType.STROLLER) {
        yield put(profileProductsSearchProductsApi.request(productType, term));

        try {
            const response: ApiResponseSearchProducts = yield call(() =>
                xhr.get(strollers_api_suggest_strollers2_url(), { searchTerm: term }),
            );
            yield put(
                profileProductsSearchProductsApi.success(productType, term, response.body.suggestedStrollerOptions),
            );
        } catch (error) {
            yield handleXHRGetErrorSaga(error as XHRGetError, logger);
            yield put(profileProductsSearchProductsApi.failure(productType, term, normalizeError(error)));
        }
    }
}

export default function* root() {
    yield all([
        takeEvery(PROFILE_PRODUCTS_REMOVE_PRODUCT, removeProduct),
        takeEvery(PROFILE_PRODUCTS_ADD_PRODUCT, addProduct),
        takeLatest(PROFILE_PRODUCTS_SEARCH_PRODUCTS, searchProducts),
    ]);
}
